import {
  AccessibleOutlined,
  ApartmentOutlined,
  EmailOutlined,
  HomeOutlined,
  Phone,
  SecurityOutlined,
  WcOutlined,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import HospitalDialog from '../../common/dialog/commonDialogBox';
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import {
  fetchDoctor,
  resetCurrentDoctor,
  updateDoctor,
} from "../../redux/doctor/doctorAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import { fetchSpecialists } from "../../redux/specialist/specialistAction";
import { fetchWards } from "../../redux/ward/wardAction";

function EditDoctor() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const currentPage = 1; 
  const prevBlockIdRef = useRef(null);
  const prevFloorIdRef = useRef(null);
  
  const doctorState = useSelector((state) => state?.doctor?.updateDoctor || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const specialistList = useSelector(
    (state) => state?.specialist?.fetchSpecialists || {}
  );
  const doctorStates = useSelector((state) => state?.doctor?.fetchDoctor?.data || {});
  const loginValues = useSelector((state) => state.values?.loginValues || []);
  const userInfo = loginValues[0]?.user || {};


  useEffect(() => {
    dispatch(fetchDoctor(userInfo.doctor_id));
    dispatch(fetchDepartments());
    dispatch(fetchBlocks());
    dispatch(fetchSpecialists(1));
  }, [dispatch, id]);


  useEffect(() => {
    const doctorData = doctorStates.data || {};
    if (doctorData && Object.keys(doctorData).length > 0) {
      setValue("doctor_id", doctorData.doctor_id || "");
      setValue("doctor_name", doctorData.doctor_name || "");
      setValue("email", doctorData.email || "");
      setValue("phone", doctorData.phone || "");
      setValue("block_id", doctorData.block_id || "");
      setValue("gender_type", doctorData.gender_type || "");
      setValue("ward_id", doctorData.ward_id || "");
      setValue("floor_id", doctorData.floor_id || "");
      setValue("specialization_id", doctorData.specialization_id || "");
      setValue("address", doctorData.address || "");
      setValue("status", doctorData?.User?.status || "");
      

      if (doctorData.block_id) {
        prevBlockIdRef.current = doctorData.block_id;
        dispatch(fetchFloors(doctorData.block_id, currentPage));
      }
      

      if (doctorData.block_id && doctorData.floor_id) {
        prevFloorIdRef.current = doctorData.floor_id;
        dispatch(fetchWards(doctorData.block_id, doctorData.floor_id, currentPage));
      }
    }
  }, [doctorStates, setValue, dispatch]);

  const watchBlock = watch("block_id");
  const watchFloor = watch("floor_id");


  useEffect(() => {
    if (watchBlock && watchBlock !== prevBlockIdRef.current) {
      dispatch(fetchFloors(watchBlock, currentPage));
      prevBlockIdRef.current = watchBlock;

      setValue("floor_id", "");
      setValue("ward_id", "");
      prevFloorIdRef.current = null;
    }
  }, [watchBlock, dispatch, setValue]);
  

  useEffect(() => {
    if (watchBlock && watchFloor && watchFloor !== prevFloorIdRef.current) {
      dispatch(fetchWards(watchBlock, watchFloor, currentPage));
      prevFloorIdRef.current = watchFloor;
    }
  }, [watchFloor, watchBlock, dispatch]);


  useEffect(() => {
    if (doctorState.error) {
      clearErrors();
      
      if (doctorState.error === "email must be unique") {
        setError("email", {
          type: "manual",
          message: "Email already exists",
        });
      } else if (doctorState.error === "phone must be unique") {
        setError("phone", {
          type: "manual",
          message: "Phone number already exists",
        });
      } else if (doctorState.errorMessage || doctorState.errormessage || doctorState.message) {
        setDialogMessage(doctorState.errorMessage || doctorState.errormessage || doctorState.message);
        setIsSuccess(false);
        setOpenDialog(true);
      } else {
        setDialogMessage(doctorState.error);
        setIsSuccess(false);
        setOpenDialog(true);
      }
      dispatch(resetCurrentDoctor());
    } else if (doctorState?.success) {
      setDialogMessage("Profile Update successfully!");
      setIsSuccess(true);
      setOpenDialog(true);
      dispatch(resetCurrentDoctor());
    }
  }, [doctorState, setError, clearErrors, reset, dispatch]);

  const inputFields = useMemo(
    () => [
      {
        name: "doctor_id",
        label: "Doctor Id",
        placeholder: "Enter Doctor Id",
        type: "text",
        rules: { required: "Doctor Id is required" },
        startIcon: <SecurityOutlined />,
      },
      {
        name: "doctor_name",
        label: "Doctor Name",
        placeholder: "Enter name",
        type: "text",
        rules: { required: "Name is required" },
        startIcon: <SecurityOutlined />,
      },
      {
        name: "email",
        label: "Email",
        placeholder: "Enter email",
        type: "email",
        rules: { required: "Email is required" },
        startIcon: <EmailOutlined />,
      },
      {
        name: "phone",
        label: "Phone Number",
        placeholder: "Enter phone number",
        type: "tel",
        maxLength: 10, 
        showCharCount: false, 
        rules: {
          required: "Phone number is required",
          pattern: {
            value: /^(6|7|8|9)\d{9}$/,
            message: "Invalid phone number",
          },
          maxLength: {
            value: 10,
            message: "Phone number should be 10 digits",
          },
        },
        startIcon: <Phone />,
      },
      {
        name: "block_id",
        label: "Block",
        placeholder: "Select Block",
        type: "select",
        readOnly: true,  
        options:
          blockList?.Blocks?.data?.map((block) => ({
            value: block.id,
            label: block.block_name,
          })) || [],
        rules: { required: "Block is required" },
        startIcon: <ApartmentOutlined />,
      },
      {
        name: "gender_type",
        label: "Gender",
        placeholder: "Select gender",
        type: "select",
        options: [
          { value: "M", label: "Male" },
          { value: "F", label: "Female" },
          { value: "Other", label: "Other" },
        ],
        rules: { required: "Gender is required" },
        startIcon: <WcOutlined />,
      },

      {
        name: "floor_id",
        label: "Floor",
        placeholder: "Select Floor",
        type: "select",
        readOnly: true,  
        options:
          floorList?.floors?.data?.map((floor) => ({
            value: floor.id,
            label: floor.floor_name,
          })) || [],
        rules: { required: "Floor is required" },
        startIcon: <ApartmentOutlined />,
        disabled: !watchBlock,
      },
      {
        name: "address",
        label: "Address",
        placeholder: "Enter address",
        type: "text",
        readOnly: true,  
        rules: { required: "Address is required" },
        startIcon: <HomeOutlined />,
      },
      {
        name: "ward_id",
        label: "Ward Name",
        placeholder: "Select Ward",
        type: "select",
        readOnly: true,  
        options:
          wardList?.wards?.data?.map((ward) => ({
            value: ward.id,
            label: ward.ward_name,
          })) || [],
        rules: { required: "Ward name is required" },
        startIcon: <HomeOutlined />,
        disabled: !watchBlock || !watchFloor,
      },
      {
        name: "specialization_id",
        label: "Specialist",
        placeholder: "Select Specialist",
        type: "select",
        readOnly: true,  
        options:
          specialistList?.specialists?.data?.map((specialist) => ({
            value: specialist.id,
            label: specialist.Name,
          })) || [],
        rules: { required: "Specialist is required" },
        startIcon: <AccessibleOutlined />,
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Select status",
        type: "select",
        readOnly: true,  
        options: [
          { value: 1, label: "Active" },
          { value: 2, label: "Inactive" },
        ],
        rules: { required: "Status is required" },
        startIcon: <AccessibleOutlined />,
      },
    ],
    [blockList, floorList, wardList, specialistList, watchBlock, watchFloor]
  );

  const onSubmit = (data) => {
    const formData = { ...data };
    dispatch(updateDoctor(userInfo.doctor_id, formData));
  };

  const handleCancel = () => {
    navigate(APP_LINK.LISTDOCTOR);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (isSuccess) {
      navigate(APP_LINK.LISTDOCTOR);
    }
  };
  
  const handleBack = () => {
    navigate(APP_LINK.LISTDOCTOR);
  };
  const navigateToDoctorList = () => {
    navigate(APP_LINK.LISTDOCTOR);
    }

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper} elevation={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={8} lg={9} xl={11}>
            <Typography variant="h3" sx={styles.heading}>
              My Profile
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={3} xl={1} mb={2}>
            <Button
              variant="contained"
              color="error"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReusableFormFields
                inputFields={inputFields}
                register={register}
                errors={errors}
                setValue={setValue}
                gridProps={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
                control={control}
              />
            </Grid>
          </Grid>

          <Box sx={styles.buttonsContainer}>
            <CustomButton
              type="button"
              text="Back"
              variant="contained"
              disableElevation
              size="small"
              onClick={handleCancel}
            />
            <CustomButton
              type="submit"
              text="Submit"
              variant="contained"
              disableElevation
              size="small"
              disabled={doctorState?.inProgress}
              loading={doctorState?.inProgress}
            />
          </Box>
        </form>
      </Paper>
      <HospitalDialog
        open={openDialog}
        onClose={handleCloseDialog}
        isSuccess={isSuccess}
        dialogMessage={dialogMessage}
        title={isSuccess ? 'Doctor Update' : 'Error'}
        navigateTo={navigateToDoctorList}
      />
    </Box>
  );
}

export default EditDoctor;